import React, { useState } from 'react';
import {
    Box,
    Button,
    Container,
    MenuItem,
    Select,
    Typography,
    FormControl,
    InputLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Modal,
    IconButton,
    TablePagination,
    Chip,
    TextField,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
// import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
// import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
// import DevicesIcon from '@mui/icons-material/Devices';
// import TuneIcon from '@mui/icons-material/Tune';
import ErrorIcon from '@mui/icons-material/Error';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import BookmarkIcon from '@mui/icons-material/Bookmark';

// ─── Mock Data ────────────────────────────────────────────────────────────────

const summaryCards = [
    { label: 'HO Collection', value: '2,450,000', color: '#1976d2' },
    { label: 'Payable to Depot', value: '1,980,000', color: '#1976d2' },
    { label: 'Unused Travel Balance', value: '450,000', color: '#ed6c02' },
    { label: 'Govt. Pass Receivable', value: '260,000', color: '#2e7d32' },
    { label: 'HGH Payable', value: '190,000', color: '#1976d2' },
    { label: 'Pending Refunds', value: '775,000', color: '#ed6c02' },
    { label: 'Settlement Prepared This Month', value: '775,000', color: '#1976d2' },
    { label: 'Unreconciled Balance', value: '20,000', color: '#d32f2f' },
];

const depotRevenueRows = [
    { id: 1, depot: '0364', onlineBooking: '125,000', onboardOnline: '185,000', cashCollected: '245,000', travelCards: '85,000', seasonTickets: '43,456', govPasses: '21,230', clergyPasses: '42,123', refunds: '1,020', total: '75,044' },
    { id: 2, depot: '0332', onlineBooking: '135,000', onboardOnline: '115,000', cashCollected: '325,000', travelCards: '87,740', seasonTickets: '13,456', govPasses: '24,000', clergyPasses: '25,123', refunds: '2,000', total: '95,023' },
];

const depotEfficiencyRows = [
    { id: 1, depot: '0364', buses: 54, passengers: '251,110', km: '1,235 km', revenuePerKm: '4,250/km' },
    { id: 2, depot: '0321', buses: 102, passengers: '451,110', km: '8,235 km', revenuePerKm: '7,232/km' },
];

const channelSnapshotRows = [
    { id: 1, channel: 'Online Booking', today: '—', mtd: '—', ytd: '—', pendingAllocation: '—' },
    { id: 2, channel: 'Online Onboard', today: '—', mtd: '—', ytd: '—', pendingAllocation: '—' },
    { id: 3, channel: 'Travel Cards', today: '—', mtd: '—', ytd: '—', pendingAllocation: '—' },
    { id: 4, channel: 'Govt Pass', today: '—', mtd: '—', ytd: '—', pendingAllocation: '—' },
];

const attentionRows = [
    { id: 1, type: 'Exception', ref: 'INV-7584', description: 'Discrepancy in online booking settlement', amount: '45,000', age: '3 days', priority: 'High' },
];

// ─── Reusable pagination hook ─────────────────────────────────────────────────

const usePagination = (defaultRows = 10) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(defaultRows);
    return {
        page,
        rowsPerPage,
        handleChangePage: (_, p) => setPage(p),
        handleChangeRowsPerPage: (e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); },
        startIndex: page * rowsPerPage,
    };
};

// ─── Component ────────────────────────────────────────────────────────────────

const FinancePage = () => {

    // Filter state
    const [dateRange, setDateRange] = useState('');
    const [depot, setDepot] = useState('');
    const [channel, setChannel] = useState('');
    const [status, setStatus] = useState('');

    // Resolve modal
    const [resolveOpen, setResolveOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const handleResolveOpen = (item) => { setCurrentItem(item); setResolveOpen(true); };
    const handleResolveClose = () => { setCurrentItem(null); setResolveOpen(false); };

    // Paginations (one per table, same pattern as sample)
    const revPag = usePagination();
    const effPag = usePagination();
    const chanPag = usePagination();
    const attPag = usePagination();

    // Shared cell styles — identical to sample code
    const thSx = { py: 1 };
    const tdSx = { py: 0 };

    return (
        <Container component="main" maxWidth="full" sx={{ py: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>

                {/* ── Page heading row ── */}
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 3, flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        Finance
                    </Typography>
                    <IconButton size="small"><MoreHorizIcon /></IconButton>
                </Box>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                        gap: 2,
                        width: '100%',
                        mb: 3
                    }}
                >
                    {summaryCards.map((card) => (
                        <Paper
                            key={card.label}
                            variant="outlined"
                            sx={{ p: 2, borderRadius: 2 }}
                        >
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                {card.label}
                            </Typography>
                            <Typography
                                variant="h6"
                                sx={{ fontWeight: 700, color: card.color }}
                            >
                                {card.value}
                            </Typography>
                        </Paper>
                    ))}
                </Box>

                {/* ── Filter bar ── */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                        gap: 1.5,
                        width: '100%',
                        mb: 3,
                        alignItems: 'center'
                    }}
                >
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FilterListIcon />}
                        sx={{
                            borderRadius: '4px',
                            textTransform: 'none',
                            fontWeight: 600,
                            height: '40px'
                        }}
                    >
                        Filters
                    </Button>

                    <FormControl size="small" fullWidth>
                        <InputLabel>Date Range</InputLabel>
                        <Select value={dateRange} onChange={e => setDateRange(e.target.value)} label="Date Range">
                            <MenuItem value="today">Today</MenuItem>
                            <MenuItem value="this_week">This Week</MenuItem>
                            <MenuItem value="this_month">This Month</MenuItem>
                            <MenuItem value="this_year">This Year</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" fullWidth>
                        <InputLabel>Depot</InputLabel>
                        <Select value={depot} onChange={e => setDepot(e.target.value)} label="Depot">
                            <MenuItem value="0364">0364</MenuItem>
                            <MenuItem value="0332">0332</MenuItem>
                            <MenuItem value="0321">0321</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" fullWidth>
                        <InputLabel>Channel</InputLabel>
                        <Select value={channel} onChange={e => setChannel(e.target.value)} label="Channel">
                            <MenuItem value="online_booking">Online Booking</MenuItem>
                            <MenuItem value="online_onboard">Online Onboard</MenuItem>
                            <MenuItem value="travel_cards">Travel Cards</MenuItem>
                            <MenuItem value="govt_pass">Govt Pass</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select value={status} onChange={e => setStatus(e.target.value)} label="Status">
                            <MenuItem value="settled">Settled</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="unreconciled">Unreconciled</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                {/* ══════════════════════════════════════════
                    TABLE 1 — Depot Revenue
                ══════════════════════════════════════════ */}
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 1, flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Depot Revenue</Typography>
                </Box>

                <TableContainer component={Paper} sx={{ width: '100%' }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#7cdffa4b' }}>
                                <TableCell sx={thSx}>Depot</TableCell>
                                <TableCell sx={thSx}>Online Booking</TableCell>
                                <TableCell sx={thSx}>Onboard Online</TableCell>
                                <TableCell sx={thSx}>Cash Collected</TableCell>
                                <TableCell sx={thSx}>Travel Cards</TableCell>
                                <TableCell sx={thSx}>Season Tickets</TableCell>
                                <TableCell sx={thSx}>Government Passes</TableCell>
                                <TableCell sx={thSx}>Clergy Passes</TableCell>
                                <TableCell sx={thSx}>Refunds</TableCell>
                                <TableCell sx={thSx}>Total Revenue</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {depotRevenueRows
                                .slice(revPag.startIndex, revPag.startIndex + revPag.rowsPerPage)
                                .map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell sx={tdSx}>{row.depot}</TableCell>
                                        <TableCell sx={tdSx}>{row.onlineBooking}</TableCell>
                                        <TableCell sx={tdSx}>{row.onboardOnline}</TableCell>
                                        <TableCell sx={tdSx}>{row.cashCollected}</TableCell>
                                        <TableCell sx={tdSx}>{row.travelCards}</TableCell>
                                        <TableCell sx={tdSx}>{row.seasonTickets}</TableCell>
                                        <TableCell sx={tdSx}>{row.govPasses}</TableCell>
                                        <TableCell sx={tdSx}>{row.clergyPasses}</TableCell>
                                        <TableCell sx={tdSx}>{row.refunds}</TableCell>
                                        <TableCell sx={{ ...tdSx, fontWeight: 700 }}>{row.total}</TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        showFirstButton showLastButton
                        component="div"
                        count={depotRevenueRows.length}
                        page={revPag.page}
                        onPageChange={revPag.handleChangePage}
                        rowsPerPage={revPag.rowsPerPage}
                        onRowsPerPageChange={revPag.handleChangeRowsPerPage}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </TableContainer>

                {/* ══════════════════════════════════════════
                    TABLE 2 — Depot Efficiency
                ══════════════════════════════════════════ */}
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 4, flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Depot Efficiency</Typography>
                </Box>

                <TableContainer component={Paper} sx={{ width: '100%' }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#7cdffa4b' }}>
                                <TableCell sx={thSx}>Depot</TableCell>
                                <TableCell sx={thSx}>No of Buses</TableCell>
                                <TableCell sx={thSx}>No of Passengers</TableCell>
                                <TableCell sx={thSx}>No of KM</TableCell>
                                <TableCell sx={thSx}>Revenue / KM</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {depotEfficiencyRows
                                .slice(effPag.startIndex, effPag.startIndex + effPag.rowsPerPage)
                                .map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell sx={tdSx}>{row.depot}</TableCell>
                                        <TableCell sx={tdSx}>{row.buses}</TableCell>
                                        <TableCell sx={tdSx}>{row.passengers}</TableCell>
                                        <TableCell sx={tdSx}>{row.km}</TableCell>
                                        <TableCell sx={tdSx}>{row.revenuePerKm}</TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        showFirstButton showLastButton
                        component="div"
                        count={depotEfficiencyRows.length}
                        page={effPag.page}
                        onPageChange={effPag.handleChangePage}
                        rowsPerPage={effPag.rowsPerPage}
                        onRowsPerPageChange={effPag.handleChangeRowsPerPage}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </TableContainer>

                {/* ══════════════════════════════════════════
                    TABLE 3 — Channel Collections Snapshot
                ══════════════════════════════════════════ */}
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 4, flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Channel Collections Snapshot</Typography>
                </Box>

                <TableContainer component={Paper} sx={{ width: '100%' }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#7cdffa4b' }}>
                                <TableCell sx={thSx}>Channel</TableCell>
                                <TableCell sx={thSx}>Today</TableCell>
                                <TableCell sx={thSx}>MTD</TableCell>
                                <TableCell sx={thSx}>YTD</TableCell>
                                <TableCell sx={thSx}>Pending Allocation</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {channelSnapshotRows
                                .slice(chanPag.startIndex, chanPag.startIndex + chanPag.rowsPerPage)
                                .map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell sx={{ ...tdSx, fontWeight: 600 }}>{row.channel}</TableCell>
                                        <TableCell sx={{ ...tdSx, color: '#999' }}>{row.today}</TableCell>
                                        <TableCell sx={{ ...tdSx, color: '#999' }}>{row.mtd}</TableCell>
                                        <TableCell sx={{ ...tdSx, color: '#999' }}>{row.ytd}</TableCell>
                                        <TableCell sx={{ ...tdSx, color: '#999' }}>{row.pendingAllocation}</TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        showFirstButton showLastButton
                        component="div"
                        count={channelSnapshotRows.length}
                        page={chanPag.page}
                        onPageChange={chanPag.handleChangePage}
                        rowsPerPage={chanPag.rowsPerPage}
                        onRowsPerPageChange={chanPag.handleChangeRowsPerPage}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </TableContainer>

                {/* ══════════════════════════════════════════
                    TABLE 4 — Immediate Attention
                ══════════════════════════════════════════ */}
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 4, flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Immediate Attention</Typography>
                </Box>

                <TableContainer component={Paper} sx={{ width: '100%' }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#7cdffa4b' }}>
                                <TableCell sx={thSx}>Type</TableCell>
                                <TableCell sx={thSx}>Ref</TableCell>
                                <TableCell sx={thSx}>Description</TableCell>
                                <TableCell sx={thSx}>Amount</TableCell>
                                <TableCell sx={thSx}>Age</TableCell>
                                <TableCell sx={thSx}>Priority</TableCell>
                                <TableCell sx={thSx} align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {attentionRows
                                .slice(attPag.startIndex, attPag.startIndex + attPag.rowsPerPage)
                                .map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell sx={tdSx}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <ErrorIcon sx={{ color: '#d32f2f', fontSize: 16 }} />
                                                <Typography variant="body2">{row.type}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={tdSx}>{row.ref}</TableCell>
                                        <TableCell sx={tdSx}>{row.description}</TableCell>
                                        <TableCell sx={tdSx}>{row.amount}</TableCell>
                                        <TableCell sx={tdSx}>{row.age}</TableCell>
                                        <TableCell sx={tdSx}>
                                            <Chip
                                                label={row.priority}
                                                size="small"
                                                icon={<BookmarkIcon sx={{ '&&': { color: '#fff' } }} />}
                                                sx={{ backgroundColor: '#ed6c02', color: '#fff', fontWeight: 600, fontSize: 11 }}
                                            />
                                        </TableCell>
                                        <TableCell sx={tdSx} align="right">
                                            <Button
                                                variant="contained"
                                                size="small"
                                                onClick={() => handleResolveOpen(row)}
                                                sx={{
                                                    fontWeight: 'bold',
                                                    borderRadius: '4px',
                                                    backgroundColor: '#3f51b5',
                                                    color: '#fff',
                                                    '&:hover': { backgroundColor: '#303f9f' },
                                                }}
                                            >
                                                Resolve
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        showFirstButton showLastButton
                        component="div"
                        count={attentionRows.length}
                        page={attPag.page}
                        onPageChange={attPag.handleChangePage}
                        rowsPerPage={attPag.rowsPerPage}
                        onRowsPerPageChange={attPag.handleChangeRowsPerPage}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </TableContainer>

                {/* ══════════════════════════════════════════
                    Resolve Modal — same style as Edit modal in sample
                ══════════════════════════════════════════ */}
                <Modal open={resolveOpen} onClose={handleResolveClose}>
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 400,
                        bgcolor: 'background.paper',
                        border: '2px solid gray',
                        boxShadow: 24,
                        p: 4,
                        borderRadius: '10px',
                    }}>
                        <Typography variant="h6" gutterBottom>Resolve Exception</Typography>

                        <TextField fullWidth disabled label="Reference" value={currentItem?.ref || ''} sx={{ marginBottom: '16px' }} />
                        <TextField fullWidth disabled label="Description" value={currentItem?.description || ''} sx={{ marginBottom: '16px' }} />
                        <TextField fullWidth disabled label="Amount" value={currentItem?.amount || ''} sx={{ marginBottom: '16px' }} />
                        <TextField fullWidth multiline rows={3} label="Resolution Note" placeholder="Enter resolution details..." sx={{ marginBottom: '16px' }} />

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button variant="contained" color="primary" onClick={handleResolveClose} sx={{ marginRight: '8px' }}>
                                Confirm
                            </Button>
                            <Button variant="contained" onClick={handleResolveClose} sx={{ backgroundColor: 'gray' }}>
                                Cancel
                            </Button>
                        </Box>
                    </Box>
                </Modal>

            </Box>
        </Container>
    );
};

export default FinancePage;